import React, { useState, useEffect, useCallback } from "react";
import { Select, Spin } from "antd";
import debounce from "lodash/debounce";
import { ComboConfig, FormColumnConfig } from "../../../interfaces/entity";

const { Option } = Select;

interface AsyncSelectProps {
    fetchOptions: (page: number, search: string) => Promise<{ data: any[]; hasMore: boolean }>;
    placeholder?: string;
    value?: any;
    debounceTime?: number;
    combo?: ComboConfig;
    column?: FormColumnConfig;
    onChange?: (value: any) => void;
    style?: React.CSSProperties;
}

const AsyncSelect: React.FC<AsyncSelectProps> = ({
    fetchOptions,
    placeholder = "Select an option",
    value,
    debounceTime = 300,
    combo,
    column,
    onChange,
    ...rest
}) => {
    const [options, setOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [search, setSearch] = useState("");



    const loadOptions = useCallback(
        async (pageToLoad: number, searchText: string = "") => {
            setLoading(true);
            try {
                const result = await fetchOptions(pageToLoad, searchText);
                setOptions((prev) => (pageToLoad === 1 ? result.data : [...prev, ...result.data]));
                setHasMore(result.hasMore);
            } finally {
                setLoading(false);
            }
        },
        [fetchOptions]
    );

    const debouncedSearch = useCallback(
        debounce((searchText) => {
            setPage(1);
            setSearch(searchText);
            loadOptions(1, searchText);
        }, debounceTime),
        [debounceTime, loadOptions]
    );

    useEffect(() => {
        // Load initial options on mount
        if (options.length === 0 && !loading) {
            loadOptions(1, search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (combo?.rerender?.length) {
            setPage(1);
            setOptions([]);
            loadOptions(1, search);
        }
    }, [combo?.rerender?.join(",")]);

    const handleSearch = (value: string) => {
        debouncedSearch(value);
    };

    const handleDropdownVisibleChange = (open: boolean) => {
        if (open && options.length === 0) {
            loadOptions(1, search);
        }
    };

    useEffect(() => {
        const dropdown: any = document.querySelector('.ant-select-dropdown');
        if (dropdown) {
            dropdown.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (dropdown) {
                dropdown.removeEventListener('scroll', handleScroll);
            }
        };
    }, [options, hasMore, loading, page]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const target = event.target as HTMLDivElement;
        console.log("targettarget", target)
        if (target.scrollTop + target.offsetHeight === target.scrollHeight && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadOptions(nextPage, search);
        }
    };

    return (
        <Select
            showSearch
            filterOption={false}
            placeholder={placeholder}
            value={value}
            onSearch={handleSearch}
            onChange={onChange}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            style={{ width: '100%', minWidth: 200, ...rest.style }}
            notFoundContent={loading ? <Spin size="small" /> : 'No data'}
            dropdownRender={(menu) => (
                <div onScroll={handleScroll}>
                    {menu}
                    {loading && (
                        <div style={{ textAlign: "center", padding: "8px" }}>
                            <Spin size="small" />
                        </div>
                    )}
                </div>
            )}
            {...rest}
        >
            {column?.extraOptions?.map((option) => (
                <Option
                    key={option.value}
                    value={option.value}
                    label={option.label || option.name}
                >
                    {option.name}
                </Option>
            ))}
            {options.map((option) => (
                <Option
                    key={option.value}
                    value={option.value}
                    label={option.label}
                >
                    {option.optionLabel || option.label}
                </Option>
            ))}
        </Select>
    );
};

export default AsyncSelect;
